/**
 * One question of a step on one line: the coloured icon + label (and its
 * ⓘ) in a fixed column, the control, and an optional tag on the right
 * (where the setting comes from).
 * Every row shares the same columns, so a step reads straight down
 * (Schedule, Hour Optimizer).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { HintDot } from '@/components/ui';

interface Props {
  icon: LucideIcon;
  label: string;
  hint?: string;
  /** Right-aligned beside the control (an inheritance tag). */
  aside?: ReactNode;
  children: ReactNode;
}

export const StepRow: FC<Props> = ({ icon: Icon, label, hint, aside, children }) => (
  <div className="grid grid-cols-1 items-start gap-x-6 gap-y-2 py-3.5 first:pt-0 last:pb-0 sm:grid-cols-[150px_minmax(0,1fr)_auto]">
    <span className="flex h-8 items-center gap-2 whitespace-nowrap text-[13px] font-medium text-foreground">
      <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      {label}
      {hint ? <HintDot label={hint} /> : null}
    </span>
    <div className="flex min-w-0 flex-col gap-2">{children}</div>
    {aside ? <div className="flex h-8 items-center sm:justify-end">{aside}</div> : <span className="hidden sm:block" />}
  </div>
);

/** The line under a choice that says what it means — it fades in when the choice changes. */
export const ValueLine: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2 duration-300 animate-in fade-in-0 slide-in-from-top-1 motion-reduce:animate-none">
    {children}
  </div>
);
